import { Bell, Mail, Shield, ShieldCheck, ShieldOff } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '@/nucleo/auth/useAuth';
import { rotuloDoPapel } from '@/nucleo/auth/papeis';
import { iniciais } from '@/nucleo/navegacao/apresentacao';
import { rotuloDoTipoDeCredenciado } from '@/nucleo/pessoaNatureza';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { Avatar, AvatarFallback } from '@/ui/base/avatar';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Skeleton } from '@/ui/base/skeleton';

import { EditarMeusDados } from './EditarMeusDados';
import { SegundoFatorCard } from './SegundoFatorCard';

/** As áreas com "Meu perfil", cada uma dentro da própria casca. */
const RAIZES = ['/admin', '/operacao', '/sme', '/credenciado'] as const;
type RaizDaArea = (typeof RAIZES)[number];

/** A área em que a tela foi aberta, pelo endereço — a casca em volta é dela. */
function raizDaArea(caminho: string): RaizDaArea {
  return RAIZES.find((raiz) => caminho === raiz || caminho.startsWith(`${raiz}/`)) ?? '/operacao';
}

/**
 * "Meu perfil" — `/admin/perfil`, `/operacao/perfil`, `/sme/perfil` (SME e DRE)
 * e `/credenciado/perfil`: quem está conectado, com que e-mail e com que
 * perfis de acesso; o método do segundo fator e a troca; e o caminho para as
 * preferências de notificação.
 *
 * UM COMPONENTE, QUATRO ÁREAS, dentro da casca de cada uma (decisão do dono em
 * 2026-09-11 para as três primeiras; o Portal do Credenciado entrou em
 * 05/10/2026, ordem M3). O protótipo tem uma rota só (`/perfil`); aqui cada
 * área tem casca própria, e uma rota solta abriria a tela sem barra lateral
 * nem cabeçalho. A tela é destino do menu da conta, no mesmo lugar em todas
 * (`MenuDaConta.tsx`, `ItensDaConta`), e não item de menu
 * (`ROTAS_FORA_DO_MENU`, em `nucleo/navegacao/mapa.ts`).
 *
 * CADA ÁREA VÊ SÓ O QUE SE APLICA. Tudo o que a tela mostra vale para todo
 * perfil — identidade, perfis de acesso, segundo fator e preferências de
 * notificação, que existem nas quatro áreas. O que muda é o rótulo: no Portal
 * do Credenciado, "Perfil de acesso no credenciado", e o tipo de cadastro
 * (pessoa física, jurídica ou cooperativa), quando o `GET /eu` o traz.
 *
 * TUDO O QUE ESTA TELA MOSTRA SAI DO `GET /eu` e do `GET /eu/segundo-fator`,
 * que existem e são reais (`apps/api/src/modulos/eu/eu.controller.ts`). Não há
 * dado simulado aqui. "Editar meus dados" grava nome e sobrenome pelo
 * `PATCH /eu/dados` (pedido do dono, 05/10/2026); o e-mail não muda por aqui.
 *
 * O QUE NÃO VEIO DO PROTÓTIPO, E POR QUÊ. Pela regra 7 de interface ("o que o
 * usuário não pode fazer não é renderizado"), ficaram de fora — e voltam junto
 * com o endpoint de cada um:
 *
 *   Cargo e "Salvar Perfil"                         não há onde gravar cargo; o nome
 *                                                   veio, em "Editar meus dados"
 *   Alterar Senha                                   senha é do Keycloak, que
 *                                                   aplica a política do realm
 *   Telefone para SMS/WhatsApp                      não há API
 *   Dispositivos registrados (PUSH)                 não há API
 *   "Conta criada em" e "Último acesso"             o GET /eu não devolve
 *   Sessões ativas                                  nenhuma área tem; não há
 *                                                   rota que liste as sessões
 *                                                   da própria pessoa
 *
 * Há teste garantindo que nada disso existe hoje (`PerfilPage.spec.tsx`).
 */
export function PerfilPage() {
  const { estado, usuario } = useAuth();
  const { pathname } = useLocation();
  const raiz = raizDaArea(pathname);
  const doCredenciado = raiz === '/credenciado';

  const cabecalho = (
    <PageHeader
      title="Meu perfil"
      description="Sua conta no Sistema TEG: identidade, perfis de acesso, segundo fator e notificações."
    />
  );

  if (estado === 'iniciando' || estado === 'entrando') {
    return (
      <div className="w-full">
        {cabecalho}
        <div className="space-y-4" aria-busy="true" aria-label="Carregando o perfil">
          <Skeleton className="h-28 w-full rounded-2xl" />
          <Skeleton className="h-44 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  // `RotaProtegida` já garante sessão antes de chegar aqui, então isto é o
  // caso de borda — sessão encerrada em outra aba, por exemplo. Sem dado não
  // se inventa tela: diz o que aconteceu e o que fazer.
  if (!usuario) {
    return (
      <div className="w-full">
        {cabecalho}
        <ErrorState
          variant="session-expired"
          description="Entre de novo para ver os dados da sua conta."
        />
      </div>
    );
  }

  const papeis = usuario.papeis;
  const tipoDeCadastro =
    doCredenciado && usuario.credenciadoTipo
      ? rotuloDoTipoDeCredenciado(usuario.credenciadoTipo)
      : null;

  return (
    <div className="w-full space-y-4">
      {cabecalho}

      <Card className="rounded-2xl">
        <CardContent className="pt-6">
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <Avatar className="h-16 w-16">
              <AvatarFallback className="bg-primary text-xl font-semibold text-primary-foreground">
                {iniciais(usuario.nome)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 space-y-1">
              <h2 className="text-lg font-semibold">{usuario.nome ?? 'Usuário'}</h2>
              {usuario.email && (
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="truncate">{usuario.email}</span>
                </p>
              )}
              <ul className="mt-1 flex flex-wrap gap-1.5">
                {papeis.map((papel) => (
                  <li key={papel}>
                    <Badge variant="secondary" className="text-[11px]">
                      {rotuloDoPapel(papel)}
                    </Badge>
                  </li>
                ))}
              </ul>
            </div>
            <div className="sm:ml-auto">
              <EditarMeusDados />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="h-4 w-4" aria-hidden="true" />
            Informações da conta
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-3 text-sm [&>div+div]:border-t [&>div+div]:pt-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <dt className="text-muted-foreground">Identificador do usuário</dt>
              {/* Inteiro, não cortado: é o que se cola num chamado de suporte. */}
              <dd className="break-all font-mono text-xs">{usuario.id}</dd>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <dt className="text-muted-foreground">
                {doCredenciado ? 'Perfil de acesso no credenciado' : 'Perfis de acesso'}
              </dt>
              <dd className="flex flex-wrap justify-end gap-1">
                {papeis.map((papel) => (
                  <Badge key={papel} variant="outline" className="text-[10px]">
                    {rotuloDoPapel(papel)}
                  </Badge>
                ))}
              </dd>
            </div>
            {tipoDeCadastro && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <dt className="text-muted-foreground">Tipo de cadastro</dt>
                <dd>{tipoDeCadastro}</dd>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <dt className="text-muted-foreground">Segundo fator</dt>
              <dd className="flex items-center gap-1.5">
                {usuario.mfaVerificada ? (
                  <ShieldCheck className="h-3.5 w-3.5 text-success" aria-hidden="true" />
                ) : (
                  <ShieldOff className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                )}
                {usuario.mfaVerificada ? 'Confirmado nesta sessão' : 'Não confirmado'}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <SegundoFatorCard />

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell className="h-4 w-4" aria-hidden="true" />
            Notificações
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Os avisos no sistema chegam sempre. Por e-mail, você escolhe se quer receber as
            comunicações opcionais.
          </p>
          <Button asChild variant="outline">
            <Link to={`${raiz}/perfil/notificacoes`}>Preferências de notificação</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
